import { BaseModal, FormActions } from '@practium/ui';
import { useRegionForm } from '../../hooks/useRegionForm';
import type { ReferenceFormValue, StateReferenceDTO } from '../../types/parameters.types';
import { EditRegionLeftPanel } from '../Panels/EditRegionLeftPanel';
import { MapRightPanel } from '../Panels/MapRightPanel';
import { SelectBaseLeftPanel } from '../Panels/SelectBaseLeftPanel';
import styles from './RegionFormModal.module.css';

interface RegionFormModalProps {
	references: StateReferenceDTO[];
	initialReference?: StateReferenceDTO;
	onClose: () => void;
	onConfirm: (form: ReferenceFormValue) => Promise<boolean>;
	onDelete?: () => Promise<boolean>;
}

export const RegionFormModal = ({ references, initialReference, onClose, onConfirm, onDelete }: RegionFormModalProps) => {
	const form = useRegionForm({ references, initialReference, onConfirm, onClose });
	const isEdit = !!initialReference;
	const isSelectBase = form.step === 'selectBase';

	const handleDelete = async () => {
		if (onDelete && (await onDelete())) onClose();
	};

	return (
		<BaseModal
			isOpen
			maxWidth="82.5rem"
			title={isEdit ? 'Editar região' : 'Nova região'}
			isLoading={form.isSubmitting}
			onClose={onClose}
			actions={
				isSelectBase ? (
					<FormActions onCancel={onClose} onSecondary={form.skipBase} secondaryText="Continuar sem base" />
				) : (
					<FormActions
						onCancel={isEdit ? undefined : onClose}
						onDelete={isEdit ? handleDelete : undefined}
						deleteInActions={isEdit}
						onSave={form.submit}
						isLoading={form.isSubmitting}
						saveText={isEdit ? 'Salvar' : 'Criar região'}
					/>
				)
			}
		>
			<div className={styles.body}>
				<div className={styles.leftPanel}>
					{isSelectBase ? (
						<SelectBaseLeftPanel
							references={references}
							searchQuery={form.searchQuery}
							onSearchChange={form.setSearchQuery}
							onSelectBase={form.selectBase}
						/>
					) : (
						<EditRegionLeftPanel
							selectedStateCodes={form.stateCodes}
							unavailableStateCodes={form.unavailableStateCodes}
							searchQuery={form.searchQuery}
							onSearchChange={form.setSearchQuery}
							onToggleState={form.toggleState}
						/>
					)}
				</div>
				<div className={styles.divider} />
				<div className={styles.rightPanel}>
					<MapRightPanel
						isSelectBase={isSelectBase}
						regionName={form.name}
						selectedStateCodes={form.stateCodes}
						unavailableStateCodes={form.unavailableStateCodes}
						nameError={form.errors.name}
						statesError={form.errors.stateCodes}
						onNameChange={form.changeName}
						onToggleState={form.toggleState}
					/>
				</div>
			</div>
		</BaseModal>
	);
};
